import { useI18n } from '@ecom/i18n';
import { Card, EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useDailySalesSeriesQuery, type DailySalesPoint } from '../api';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { formatCatalogPrice } from '@/features/products/format-price';

const VIEW_W = 560;
const VIEW_H = 180;
const BASELINE_Y = 170;
const TOP_Y = 14;

function xFor(index: number, count: number): number {
  if (count <= 1) return 0;
  return (index / (count - 1)) * VIEW_W;
}

function yFor(revenue: number, maxRevenue: number): number {
  if (maxRevenue <= 0) return BASELINE_Y;
  const ratio = revenue / maxRevenue;
  return BASELINE_Y - ratio * (BASELINE_Y - TOP_Y);
}

function weekdayLabel(dateIso: string): string {
  return new Intl.DateTimeFormat('fr-TN', { weekday: 'short' }).format(new Date(dateIso));
}

/**
 * "Evolution commandes / ventes" — hand-rolled SVG line chart, no charting
 * dependency (supervisor decision, Sprint UX/UI Merchant Dashboard V2:
 * "pas de nouvelle dépendance, SVG natif"), styled after
 * docs/reference/souk-dashboard.html's own sales-chart markup: three
 * gridlines, a gradient fill under a `<polyline>`, a highlighted final
 * point. Data is `useDailySalesSeriesQuery()` — real orders, CANCELLED
 * excluded, never a fabricated series.
 */
export function SalesTrendChart(): ReactElement {
  const { t } = useI18n();
  const series = useDailySalesSeriesQuery();

  if (series.isPending) return <TableSkeleton columns={1} />;
  if (series.isError) {
    return <EmptyState title={t('merchant.dashboard.salesTrend.loadError')} />;
  }

  const points = series.points;
  const maxRevenue = Math.max(...points.map((p) => p.revenue), 0);
  const totalRevenue = points.reduce((sum, p) => sum + p.revenue, 0);

  const linePoints = points
    .map(
      (point, index) =>
        `${String(xFor(index, points.length))},${String(yFor(point.revenue, maxRevenue))}`,
    )
    .join(' ');
  const fillPoints = `0,${String(BASELINE_Y)} ${linePoints} ${String(VIEW_W)},${String(BASELINE_Y)}`;

  const lastPoint = points.at(-1);

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-sm font-bold text-ink">
            {t('merchant.dashboard.salesTrend.title')}
          </h3>
          <p className="mt-0.5 text-xs text-inkFaint">
            {t('merchant.dashboard.salesTrend.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-inkFaint">
          <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
          {t('merchant.dashboard.salesTrend.legendCurrency')}
        </div>
      </div>

      {totalRevenue === 0 ? (
        <EmptyState
          title={t('merchant.dashboard.salesTrend.emptyTitle')}
          description={t('merchant.dashboard.salesTrend.emptyDescription')}
        />
      ) : (
        <>
          <svg
            viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
            className="mt-3 h-44 w-full"
            preserveAspectRatio="none"
            role="img"
            aria-label={t('merchant.dashboard.salesTrend.chartAria')}
          >
            <line
              x1="0"
              y1={TOP_Y + (BASELINE_Y - TOP_Y) * 0.15}
              x2={VIEW_W}
              y2={TOP_Y + (BASELINE_Y - TOP_Y) * 0.15}
              stroke="var(--ecom-line-soft)"
              strokeWidth="1"
            />
            <line
              x1="0"
              y1={TOP_Y + (BASELINE_Y - TOP_Y) * 0.55}
              x2={VIEW_W}
              y2={TOP_Y + (BASELINE_Y - TOP_Y) * 0.55}
              stroke="var(--ecom-line-soft)"
              strokeWidth="1"
            />
            <line
              x1="0"
              y1={BASELINE_Y}
              x2={VIEW_W}
              y2={BASELINE_Y}
              stroke="var(--ecom-line-soft)"
              strokeWidth="1"
            />
            <defs>
              <linearGradient id="dashboardSalesFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--ecom-accent)" stopOpacity="0.22" />
                <stop offset="100%" stopColor="var(--ecom-accent)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={fillPoints} fill="url(#dashboardSalesFill)" />
            <polyline
              points={linePoints}
              fill="none"
              stroke="var(--ecom-accent)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {lastPoint !== undefined ? (
              <circle
                cx={xFor(points.length - 1, points.length)}
                cy={yFor(lastPoint.revenue, maxRevenue)}
                r="4.5"
                fill="var(--ecom-accent)"
              />
            ) : null}
          </svg>
          <div className="mt-1 flex justify-between text-[10px] font-medium text-inkFaint">
            {points.map((point: DailySalesPoint, index) => {
              // Every OTHER day, counting back from the most recent one —
              // never two adjacent labels shown together, which is what
              // an even-parity rule collided into at the two most recent
              // days (the "last visible" exception and the parity rule
              // could both fire on neighbouring indices).
              const isVisible = (points.length - 1 - index) % 2 === 0;
              return (
                <span key={point.date} className={isVisible ? '' : 'invisible'}>
                  {weekdayLabel(point.date)}
                </span>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-inkFaint">
            {t('merchant.dashboard.salesTrend.totalLabel')}{' '}
            <span className="font-mono font-semibold text-ink">
              {formatCatalogPrice(totalRevenue)}
            </span>
          </p>
        </>
      )}
    </Card>
  );
}
